/**
 * components/needs/NeedsListEditor.tsx
 *
 * Needs list editor with drag-and-drop reordering.
 *
 * Feb 2026: Replaced external condition catalog with intra-form field conditions.
 * Feb 2026 (ordering): Drag-and-drop shows an amber indicator when dropping to a
 *   position that would trigger an auto-reorder. Type-change cleanup clears orphaned
 *   conditions with a toast. Highlight on source card when hovering the condition pill.
 * Feb 2026 (UX): Removed ConditionConnector (dashed line + icon) and left-bar
 *   indentation - the amber condition pill in the top row is sufficient to signal
 *   dependency. Uniform space-y-3 spacing restored between all items.
 * Feb 2026 (UX v2): Invalid drops are now blocked entirely instead of silently
 *   corrected. A contextual inline label explains the constraint during drag.
 *   Toast message is specific and names the blocking field.
 * Mar 2026: Updated for multi-condition support (conditions[] array). Cycle detection,
 *   type-change cleanup, and removal cleanup all iterate over all condition sources.
 *   highlightedFieldIds changed to Set<string> to support highlighting all source fields.
 * Apr 2026 (system email field): system fields (is_system=true) cannot be dragged or
 *   removed. Drops that would place a non-system field before a system field are blocked.
 *   Empty state shown only when there are no non-system fields; system fields always render.
 * Fix Apr 2026: empty state now checks needs.length (system+non-system) so it is suppressed
 *   when the system email is the only field present.
 * Fix Apr 2026: indexMap (useMemo) replaces needs.indexOf for O(1) position lookups;
 *   redundant system-field guard in drag-over/drop simplified.
 * Jun 2026: Added "Ajouter un champ" button (inline add) now that CatalogSidebar is removed.
 *   Updated empty state text accordingly.
 */

import { useState, useMemo } from 'react';
import { AlertCircle, Layers, Plus } from 'lucide-react';
import NeedItem from './NeedItem';
import { getOrderConflictDetails } from '../../utils/conditionOrderUtils';
import type { NeedItem as NeedItemType } from '../../types/needs';
import type { FieldType } from '../../types/database';

interface NeedsListEditorProps {
  needs: NeedItemType[];
  onNeedsChange: (needs: NeedItemType[]) => void;
  showToast: (message: string, variant?: 'success' | 'error' | 'warning' | 'info') => void;
}

function wouldCreateCycle(
  potentialSourceId: string,
  targetId: string,
  needs: NeedItemType[]
): boolean {
  const visited = new Set<string>();
  function canReach(fieldId: string): boolean {
    if (fieldId === targetId) return true;
    if (visited.has(fieldId)) return false;
    visited.add(fieldId);
    const field = needs.find((n) => n.id === fieldId);
    if (!field?.conditions) return false;
    return field.conditions.some((c) => canReach(c.source_field_id));
  }
  return canReach(potentialSourceId);
}

export default function NeedsListEditor({
  needs,
  onNeedsChange,
  showToast,
}: NeedsListEditorProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isInvalidDrop, setIsInvalidDrop] = useState(false);
  const [conflictReason, setConflictReason] = useState('');
  const [highlightedFieldIds, setHighlightedFieldIds] = useState<Set<string>>(new Set());

  const updateNeed = (index: number, updates: Partial<NeedItemType>) => {
    const currentNeed = needs[index];
    let updatedNeeds = needs.map((need, i) =>
      i === index ? { ...need, ...updates } : need
    );

    const typeChanged = updates.type !== undefined && updates.type !== currentNeed.type;
    const wasChoiceType =
      currentNeed.type === 'single_choice' || currentNeed.type === 'multiple_choice';
    const isNoLongerChoiceType =
      updates.type !== 'single_choice' && updates.type !== 'multiple_choice';

    if (typeChanged && wasChoiceType && isNoLongerChoiceType) {
      const hasDependents = updatedNeeds.some(
        (n) => n.conditions?.some((c) => c.source_field_id === currentNeed.id)
      );
      if (hasDependents) {
        updatedNeeds = updatedNeeds.map((need) => {
          if (!need.conditions) return need;
          const newConditions = need.conditions.filter(
            (c) => c.source_field_id !== currentNeed.id
          );
          return { ...need, conditions: newConditions.length > 0 ? newConditions : null };
        });
        showToast(
          'Les conditions liées à cette question ont été supprimées (type modifié)',
          'info'
        );
      }
    }

    onNeedsChange(updatedNeeds);
  };

  const removeNeed = (index: number) => {
    // System fields cannot be removed
    if (needs[index]?.is_system) return;
    const removedNeed = needs[index];
    let updatedNeeds = needs.filter((_, i) => i !== index);

    const hasDependents = updatedNeeds.some(
      (n) => n.conditions?.some((c) => c.source_field_id === removedNeed.id)
    );
    if (hasDependents) {
      updatedNeeds = updatedNeeds.map((need) => {
        if (!need.conditions) return need;
        const newConditions = need.conditions.filter(
          (c) => c.source_field_id !== removedNeed.id
        );
        return { ...need, conditions: newConditions.length > 0 ? newConditions : null };
      });
    }

    updatedNeeds.forEach((need, i) => {
      need.position = i;
    });
    onNeedsChange(updatedNeeds);
  };

  const handleDragStart = (index: number) => {
    // System fields cannot be dragged
    if (needs[index]?.is_system) return;
    setDraggedIndex(index);
    setIsInvalidDrop(false);
    setConflictReason('');
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
    if (draggedIndex !== null && draggedIndex !== index) {
      // Block drops that would place a non-system field before a system field
      const targetIsSystemOrBeforeSystem = needs[index]?.is_system;
      if (targetIsSystemOrBeforeSystem) {
        setIsInvalidDrop(true);
        setConflictReason('le champ email doit rester en première position');
        return;
      }
      const details = getOrderConflictDetails(draggedIndex, index, needs);
      setIsInvalidDrop(details.hasConflict);
      setConflictReason(details.reason);
    } else {
      setIsInvalidDrop(false);
      setConflictReason('');
    }
  };

  const handleDrop = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setIsInvalidDrop(false);
      setConflictReason('');
      return;
    }

    // Block drops on system fields
    if (needs[index]?.is_system) {
      showToast('Déplacement impossible - le champ email doit rester en première position', 'warning');
      setDraggedIndex(null);
      setDragOverIndex(null);
      setIsInvalidDrop(false);
      setConflictReason('');
      return;
    }

    if (isInvalidDrop) {
      showToast(
        conflictReason
          ? `Déplacement impossible - ${conflictReason}`
          : 'Déplacement impossible - conflit avec une condition existante',
        'warning'
      );
      setDraggedIndex(null);
      setDragOverIndex(null);
      setIsInvalidDrop(false);
      setConflictReason('');
      return;
    }

    const newNeeds = [...needs];
    const draggedNeed = newNeeds[draggedIndex];
    newNeeds.splice(draggedIndex, 1);
    newNeeds.splice(index, 0, draggedNeed);

    newNeeds.forEach((need, i) => {
      need.position = i;
    });

    onNeedsChange(newNeeds);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsInvalidDrop(false);
    setConflictReason('');
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsInvalidDrop(false);
    setConflictReason('');
  };

  const systemNeeds = needs.filter((n) => n.is_system);
  const nonSystemNeeds = needs.filter((n) => !n.is_system);

  const addEmptyField = () => {
    const newField: NeedItemType = {
      id: crypto.randomUUID(),
      type: 'short_text' as FieldType,
      label: '',
      description: null,
      is_required: false,
      position: needs.length,
      conditions: null,
      section: null,
    };
    onNeedsChange([...needs, newField]);
  };

  // O(1) position lookup - avoids O(n²) from needs.indexOf inside the render loops
  const indexMap = useMemo(
    () => new Map(needs.map((n, i) => [n.id, i])),
    [needs]
  );

  const renderNeedItem = (need: NeedItemType, index: number) => {
    const isDragOver = dragOverIndex === index && draggedIndex !== index;
    return (
      <div key={need.id}>
        <div className={`relative transition-all ${isDragOver ? 'mt-10' : ''}`}>
          {isDragOver && (
            <>
              <div
                className={`absolute -top-5 left-0 right-0 h-1 rounded-full shadow-lg ${
                  isInvalidDrop ? 'bg-amber-400' : 'bg-primary-500'
                }`}
              />
              {isInvalidDrop && conflictReason && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 translate-y-px flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium px-3 py-1 rounded-full shadow-sm whitespace-nowrap pointer-events-none z-10">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  {conflictReason}
                </div>
              )}
            </>
          )}
          <div className={draggedIndex === index ? 'opacity-50' : ''}>
            <NeedItem
              need={need}
              availableConditionFields={needs.filter(
                (n) =>
                  n.id !== need.id &&
                  (n.type === 'single_choice' || n.type === 'multiple_choice') &&
                  !wouldCreateCycle(n.id, need.id, needs)
              )}
              onUpdate={(updates) => updateNeed(index, updates)}
              onRemove={() => removeNeed(index)}
              onDragStart={(e) => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={() => handleDrop(index)}
              onDragEnd={handleDragEnd}
              isHighlighted={highlightedFieldIds.has(need.id)}
              onHighlightSource={(ids) => setHighlightedFieldIds(new Set(ids))}
              onClearHighlight={() => setHighlightedFieldIds(new Set())}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="space-y-4">
        {/* System fields always rendered at the top */}
        {systemNeeds.map((need) => {
          const index = indexMap.get(need.id)!;
          return renderNeedItem(need, index);
        })}

        {/* Non-system fields or empty state */}
        {nonSystemNeeds.length === 0 && systemNeeds.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-neutral-300 rounded-2xl bg-neutral-50">
            <Layers className="w-12 h-12 text-neutral-400 mx-auto mb-3" />
            <p className="text-neutral-600 font-medium mb-1">Aucun élément ajouté</p>
            <p className="text-sm text-neutral-500 mb-4">
              Ajoutez des champs manuellement ou importez via CSV / Excel
            </p>
            <button
              type="button"
              onClick={addEmptyField}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Ajouter un champ
            </button>
          </div>
        ) : (
          nonSystemNeeds.map((need) => {
            const index = indexMap.get(need.id)!;
            return renderNeedItem(need, index);
          })
        )}
      </div>

      <button
        type="button"
        onClick={addEmptyField}
        className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-neutral-600 hover:text-primary-600 bg-neutral-50 hover:bg-primary-50 border border-dashed border-neutral-300 hover:border-primary-300 rounded-xl transition-colors"
      >
        <Plus className="w-4 h-4" />
        Ajouter un champ
      </button>
    </div>
  );
}
